import { QuoteOrderTypeShippingInversed } from '@teinor/erp/types/company/order/quote/quoteOrder'
import { QuoteShippingModelType, QuoteShippingTypePriceInversed } from '@teinor/erp/types/company/order/quote/quoteShipping'
import { useCallback, useEffect, useMemo, useState } from 'react'
import ITHeaderText from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormElement, FormRender } from '../../../../../../../forms/FormRenderer/types'
import { parseAddressToOneLine } from '../../../../../../../utils/globals/address'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { getCurrencySymbol, prepareVatTag } from '../../../../../../../utils/globals/price'
import { returnClassOfSpanTemplate } from '../../../../../../../utils/globals/tailwind'
import { QuoteOrderShippingModelTypeExtended } from '../QuoteStepHeaderShipping'
import useOrderShippingTypePrice from '../../../../../../../utils/hooks/orders/useOrderShippingTypePrice'
import useOrderTypeShippingDict from '../../../../../../../utils/hooks/orders/useOrderTypeShippingDict'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { permissionCheck } from '../../../../../../../baseComponents/PermissionChecker/function'
import { getUserPermissions } from '../../../../../../../utils/reducers/getters'
import { displayFullNameService } from '../../SellParent/functions'

type QuoteStepHeaderShippingNoPermissionProps = InputTableLeftTabsProps & QuoteStepHeaderShippingNoPermissionLogicProps & {
}

const QuoteStepHeaderShippingNoPermission: React.FC<QuoteStepHeaderShippingNoPermissionProps> = ({ headerData, handleChange }) => {
    const { t } = useTwinTranslation()
    const { dictTypeShipping, dictTypePrice, dictShippingServices, customerAddress } = useQuoteStepHeaderShippingNoPermissionLogic({ headerData })
    const basicElements: FormElement<keyof QuoteShippingModelType | 'typeShipping'>[] = []
    const extraElements: FormElement<keyof QuoteShippingModelType>[] = []
    basicElements.push(
        {
            name: 'typeShipping',
            component: 'Custom',
            render: () => <ITHeaderText label={t('typeShipping', 'Tipo de envío')} value={dictTypeShipping[parseInt(String(headerData?.typeShipping))]?.name} className={returnClassOfSpanTemplate(2)} />
        }
    )
    if (headerData?.OrderShippingId && !headerData.typeShipping) {
        if (headerData.typeShipping === QuoteOrderTypeShippingInversed['sendToCustomer']) {
            if (customerAddress) {
                basicElements.push(
                    {
                        name: 'ShippingAddressRelationId',
                        component: 'Custom',
                        render: ({ value, extraProps }) => <ITHeaderText label={t('shippingAddress', 'Dirección de envio')} value={customerAddress} className={returnClassOfSpanTemplate(2)} />,
                        extraProps: { customerAddress }
                    },
                )
            }
            basicElements.push(
                {
                    name: 'ShippingServiceId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('shippingServices', 'Servicio de Transportista')} value={displayFullNameService(dictShippingServices, headerData?.OrderShipping?.ShippingServiceId)} className={returnClassOfSpanTemplate(2)} />,
                },
                {
                    name: 'customerPays',
                    component: 'Custom',
                    render: ({ value }) => {
                        return (
                            String(value) === 'true' ?
                                <ITHeaderText label={t('postage', 'Portes')} value={t('postageDue', 'Portes debidos')} className={returnClassOfSpanTemplate(2)} />
                                : null
                        )
                    },
                },
                {
                    name: 'numberPackages',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('numberPackages', 'Nº bultos')} value={value} className={returnClassOfSpanTemplate(1)} />,
                },
            )
            if (!headerData?.OrderShipping?.customerPays) {
                extraElements.push(
                    {
                        name: 'typePrice',
                        component: 'Custom',
                        render: ({ value }) => <ITHeaderText label={t('shippingCalculationS', 'Cálculo trans.')} value={dictTypePrice[parseInt(String(value))]?.name} className={returnClassOfSpanTemplate(1)} />,
                    }
                )
                if (headerData?.OrderShipping?.typePrice === QuoteShippingTypePriceInversed['introducePrice'] && !headerData?.hidePrices) {
                    extraElements.push(
                        {
                            name: headerData.OrderShipping.priceVatIncluded ? 'finalPriceWithTax' : 'initialPriceWithoutTax',
                            component: 'Custom',
                            render: ({ value }) => <ITHeaderText label={t('priceToCustomer', 'Precio cliente')} value={value + getCurrencySymbol(headerData.CurrencyId) + ' - ' + prepareVatTag(headerData?.OrderShipping?.priceVatIncluded, t)} className={returnClassOfSpanTemplate(1)} />
                        }
                    )
                }
            }
            extraElements.push(
                {
                    name: 'weight',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('weight', 'Peso')} value={(value === '0' || value === undefined ? '-' : value) + ' ' + t('kg', 'kg')} className={returnClassOfSpanTemplate(1)} />
                }
            )
        }
    }
    const sections: FormRender<QuoteOrderShippingModelTypeExtended> = [
        {
            cols: 2,
            elements: [
                ...basicElements,
                ...extraElements,

            ]
        }
    ]
    return (
        <InputTableLeftColumnForm form={sections} handleChange={handleChange} headerData={headerData?.OrderShipping} />
    )
}


interface QuoteStepHeaderShippingNoPermissionLogicProps {
    headerData: TwinDictionary
}

const useQuoteStepHeaderShippingNoPermissionLogic = ({ headerData }: QuoteStepHeaderShippingNoPermissionLogicProps) => {
    const ShippingAddressId = headerData?.OrderShipping?.ShippingAddressId
    const [customerAddress, setCustomerAddress] = useState<string | null>(headerData?.OrderShipping?.ShippingAddressRelation ? parseAddressToOneLine(headerData?.OrderShipping?.ShippingAddressRelation) : null)
    const dictTypeShipping = useOrderTypeShippingDict()

    const dictTypePrice = useOrderShippingTypePrice()
    const dictShippingServices = useMemo(() => dictionaryComplexFromJsonArr(headerData?.OrderShipping?.ShippingServices || [], 'ShippingServiceId'), [headerData?.OrderShipping?.ShippingServices])

    const getAllCustomerAddress = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerAddress/getCustomerAddressInstance', {  'id': ShippingAddressId  })
        if (result) {
            setCustomerAddress(parseAddressToOneLine(result))
        }
    }, [setCustomerAddress, ShippingAddressId])

    useEffect(() => {
        if (!headerData?.OrderShipping?.ShippingAddressRelation && ShippingAddressId && permissionCheck(getUserPermissions('customer.profile.address'), 'read')) {
            getAllCustomerAddress()
        }
    }, [getAllCustomerAddress, headerData?.OrderShipping?.ShippingAddressRelation, ShippingAddressId])

    return { dictTypeShipping, dictTypePrice, dictShippingServices, customerAddress }
}

export default QuoteStepHeaderShippingNoPermission